import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Plus, Minus, Compass, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMapStyleUrl } from '@/api/geo';
import type { Waypoint } from '../lib/types';

interface RouteMapProps {
  waypoints: Waypoint[];
  routeShape: [number, number][]; // [lon, lat] pairs - GeoJSON format
  isLoading: boolean;
}

// Marker colors by index
const MARKER_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

function getMarkerColor(index: number): string {
  return MARKER_COLORS[index % MARKER_COLORS.length];
}

export function RouteMap({ waypoints, routeShape, isLoading }: RouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Default center: Minsk
    const defaultCenter: [number, number] = [27.5615, 53.9006];

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: defaultCenter,
      zoom: 10,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add route source and layers
      map.current!.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
      });

      // Route casing (outline)
      map.current!.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#1e40af',
          'line-width': 8,
        },
      });

      // Route line
      map.current!.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 5,
        },
      });
    });

    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, []);

  // Update route line when shape changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const source = map.current.getSource('route') as maplibregl.GeoJSONSource;
    if (source) {
      if (routeShape.length > 0) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeShape,
          },
        });
      } else {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        });
      }
    }
  }, [routeShape, mapLoaded]);

  // Update markers when waypoints change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old markers
    for (const marker of markers.current) {
      marker.remove();
    }
    markers.current = [];

    // Add new markers
    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];
      const color = getMarkerColor(i);
      const isFirst = i === 0;
      const isLast = i === waypoints.length - 1;

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            width: ${isFirst || isLast ? '32px' : '28px'};
            height: ${isFirst || isLast ? '32px' : '28px'};
            background-color: ${color};
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${isFirst || isLast ? '14px' : '12px'};
          ">
            ${i + 1}
          </div>
          <div style="
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 8px solid ${color};
            margin-top: -2px;
          "></div>
        </div>
      `;

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
      })
        .setLngLat([wp.lon, wp.lat])
        .addTo(map.current!);

      markers.current.push(marker);
    }

    // Fit map to markers bounds
    if (waypoints.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      for (const wp of waypoints) {
        bounds.extend([wp.lon, wp.lat]);
      }

      map.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        duration: 500,
      });
    }
  }, [waypoints, mapLoaded]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    map.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    map.current?.zoomOut();
  }, []);

  const handleResetNorth = useCallback(() => {
    map.current?.easeTo({ bearing: 0, pitch: 0, duration: 300 });
  }, []);

  return (
    <div className="w-full h-full relative rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Map controls */}
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleZoomIn}
        >
          <Plus className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleZoomOut}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-8 w-8 shadow-sm"
          onClick={handleResetNorth}
        >
          <Compass className="w-4 h-4" />
        </Button>
      </div>

      {/* Empty state */}
      {waypoints.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted-foreground bg-background/80 px-4 py-2 rounded-lg">
            <p className="text-sm">Добавьте точки для отображения маршрута</p>
          </div>
        </div>
      )}
    </div>
  );
}
